/**
 * Unified All Agent Reports — merges manual/AI-generated daily reports with
 * the new site-visit reports into a single chronological feed for HODs.
 *
 * Filters:
 *   - Date range (defaults: last 7 days)
 *   - Agent (multi/single from the team's roster)
 *   - Type (All / Daily reports / Site visits)
 *
 * Each row is expandable to read the full report text or AI narrative,
 * has a Share (clipboard) button, and the toolbar exposes CSV download
 * for whichever subset is currently filtered.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import {
  FileText, MapPin, Calendar, Download, Share2, ChevronDown, ChevronRight,
  RefreshCw, Filter, CheckCircle2, XCircle, MessageSquare, Send,
} from 'lucide-react';
import { toast } from 'sonner';
import HelpInfo from './HelpInfo';
import { Textarea } from './ui/textarea';

const API = process.env.REACT_APP_BACKEND_URL;

const todayISO = () => new Date().toISOString().slice(0, 10);
const daysAgoISO = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
};
const fmtDateTime = (raw) => {
  if (!raw) return '—';
  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
};
const fmtDuration = (s) => {
  if (s == null || s < 0) return '0m';
  const m = Math.floor(s / 60);
  const h = Math.floor(m / 60);
  if (h) return `${h}h ${m % 60}m`;
  return `${m}m`;
};
const csvEscape = (v) => {
  const s = (v == null ? '' : String(v)).replace(/\r?\n/g, ' ');
  return /[",]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

// `onTotalChange(merged)`: optional callback the parent can use to keep
// its tab badge in sync. The Reports tab on HOD Dashboard was reading
// only `dailyReports.length` for its counter, which ignored the
// (often larger) site-visit feed this component fetches on its own —
// the tab said "Reports (0)" while the panel below showed 22 items.
const UnifiedReportsTab = ({ dailyReports = [], agents = [], onTotalChange }) => {
  const { token } = useAuth();
  const [from, setFrom] = useState(daysAgoISO(7));
  const [to, setTo] = useState(todayISO());
  const [agentId, setAgentId] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [visitReports, setVisitReports] = useState([]);
  // Daily reports for the entire date window. The `dailyReports` prop
  // is whatever the parent dashboard happens to have loaded (usually
  // just today's), so for the Reports panel we fetch our own copy
  // across [from, to] from /api/hod/reports?date_from=&date_to=. If
  // the fetch fails or the endpoint hasn't been upgraded yet, we
  // gracefully fall back to the parent's prop so the panel keeps
  // showing something instead of going blank.
  const [rangeDaily, setRangeDaily] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expandedKey, setExpandedKey] = useState(null);
  // Inline feedback composer — `feedbackKey` is the row whose composer
  // is open. Sending posts directly to the report's author (agent or
  // HOD) so a manager can react to a report without leaving the feed.
  const [feedbackKey, setFeedbackKey] = useState(null);
  const [feedbackText, setFeedbackText] = useState('');
  const [feedbackSending, setFeedbackSending] = useState(false);

  const fetchVisitReports = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ token });
      if (from) params.set('date_from', new Date(`${from}T00:00:00`).toISOString());
      if (to) params.set('date_to', new Date(`${to}T23:59:59`).toISOString());
      if (agentId && agentId !== 'all') params.set('agent_id', agentId);
      const res = await fetch(`${API}/api/site-visits/reports/team?${params}`);
      const data = await res.json();
      if (data.success) setVisitReports(data.reports || []);
    } catch (e) {
      // non-fatal — daily reports still render
    }
  }, [token, from, to, agentId]);

  const fetchDailyRange = useCallback(async () => {
    if (!token) return;
    try {
      const params = new URLSearchParams({ token });
      // /api/hod/reports expects plain YYYY-MM-DD here — same shape
      // it stores in `report_date`. Sending an ISO timestamp wouldn't
      // match the indexed string.
      if (from) params.set('date_from', from);
      if (to) params.set('date_to', to);
      const res = await fetch(`${API}/api/hod/reports?${params}`);
      const data = await res.json();
      if (data.success) setRangeDaily(data.data?.reports || []);
    } catch (e) {
      // leave rangeDaily as null so we fall back to the prop
    }
  }, [token, from, to]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([fetchVisitReports(), fetchDailyRange()]).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => { cancelled = true; };
  }, [fetchVisitReports, fetchDailyRange]);

  // Prefer the range fetch when we have it; fall back to whatever the
  // parent passed otherwise.
  const effectiveDaily = rangeDaily ?? dailyReports ?? [];

  // We compute two passes:
  //
  //   `scoped` = everything inside the current date range + agent
  //              filter, regardless of type. The three count tiles
  //              read off this list so the breakdown stays honest
  //              (Total = daily + visit) even after the user clicks
  //              "Daily reports" in the dropdown.
  //
  //   `merged` = `scoped` further narrowed by the type filter. This is
  //              what feeds the rows below.
  //
  // The earlier implementation derived counts from `merged`, which
  // meant choosing "Daily reports" zeroed out the Total and Site
  // visits tiles too — confusing because the tiles disagreed with the
  // dropdown's own implication ("there are 0 site visits? But I just
  // clicked away from them").
  const scoped = useMemo(() => {
    // Empty inputs ↦ no bound on that side, so Clear (which empties
    // both) gives all-time results. Without these guards
    // `new Date('T00:00:00')` returns Invalid Date and every row's
    // `ts >= NaN` comparison fails, blanking the panel.
    const fromMs = from ? new Date(`${from}T00:00:00`).getTime() : -Infinity;
    const toMs   = to   ? new Date(`${to}T23:59:59`).getTime()   : Infinity;

    const dailyRows = (effectiveDaily || []).map((r) => {
      // Prefer the stamped generation time. If that's missing, fall
      // back to the `report_date` YYYY-MM-DD string — that way a
      // report still lands inside its own day's filter window even
      // when the older code path didn't set generated_at.
      let ts = new Date(r.generated_at || r.created_at || 0).getTime();
      if (!Number.isFinite(ts) || ts === 0) {
        if (r.report_date) ts = new Date(`${r.report_date}T12:00:00`).getTime();
      }
      return {
        kind: 'daily',
        key: `daily:${r.id}`,
        id: r.id,
        ts,
        agent_id: r.agent_id || r.agent?.id,
        agent_name: r.agent?.full_name || r.agent_name || 'Unknown',
        title: 'Daily report',
        subtitle: r.report_text ? r.report_text.slice(0, 150) : '(empty)',
        // isHod — whether the report's author is an HOD/CEO. Drives the
        // separate "HOD Reports" sub-section below the agent feed.
        isHod: !!r.is_hod_author
          || /^(hod|ceo)/i.test(r.agent?.role || r.sender_role || ''),
        raw: r,
      };
    });

    const visitRows = (visitReports || []).map((v) => ({
      kind: 'visit',
      key: `visit:${v.id}`,
      id: v.id,
      ts: new Date(v.report_submitted_at || v.arrival_time || 0).getTime(),
      agent_id: v.agent_id,
      agent_name: v.agent_name || 'Unknown',
      title: v.property_name || 'Site visit',
      subtitle: v.report_outcome || (v.report_notes || '').slice(0, 150),
      isHod: !!v.is_hod_author || /^(hod|ceo)/i.test(v.sender_role || ''),
      raw: v,
    }));

    let all = [...dailyRows, ...visitRows];
    if (agentId && agentId !== 'all') {
      all = all.filter((r) => r.agent_id === agentId);
    }
    all = all.filter((r) => Number.isFinite(r.ts) && r.ts >= fromMs && r.ts <= toMs);
    all.sort((a, b) => b.ts - a.ts);
    return all;
  }, [effectiveDaily, visitReports, agentId, from, to]);

  const merged = useMemo(() => {
    if (typeFilter === 'daily') return scoped.filter((r) => r.kind === 'daily');
    if (typeFilter === 'visit') return scoped.filter((r) => r.kind === 'visit');
    return scoped;
  }, [scoped, typeFilter]);

  const counts = useMemo(() => ({
    total: scoped.length,
    daily: scoped.filter((r) => r.kind === 'daily').length,
    visit: scoped.filter((r) => r.kind === 'visit').length,
  }), [scoped]);


  // Bubble the total up to the parent so its tab badge stays accurate.
  // Effect (not direct call during render) to keep React from warning
  // about cross-component state writes during a render pass.
  useEffect(() => {
    if (onTotalChange) onTotalChange(counts);
  }, [counts, onTotalChange]);

  // Send feedback / a comment to the report's author. The recipient is
  // the report's agent_id — works whether they're an agent or an HOD,
  // so an HOD can react to an agent's report and the CEO to an HOD's.
  // It lands in the recipient's "Feedback from Management" feed.
  const sendRowFeedback = async (row) => {
    const targetId = row.agent_id;
    if (!targetId) { toast.error('This report has no linked author to message'); return; }
    if (!feedbackText.trim()) { toast.error('Write feedback before sending'); return; }
    setFeedbackSending(true);
    try {
      const res = await fetch(`${API}/api/hod/agents/${targetId}/direct-message`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ feedback_text: feedbackText, agent_id: targetId }),
      });
      const data = await res.json();
      if (!res.ok || !data?.success) {
        toast.error(data?.error || data?.detail || 'Failed to send');
        return;
      }
      toast.success(`Feedback sent to ${row.agent_name || 'author'}`);
      setFeedbackKey(null);
      setFeedbackText('');
    } catch (e) {
      toast.error(`Send failed: ${e.message || e}`);
    } finally {
      setFeedbackSending(false);
    }
  };

  const downloadCsv = () => {
    const header = ['Type', 'Submitted At', 'Agent', 'Title', 'Outcome', 'Notes / Report', 'Next Steps', 'Duration (min)'];
    const rows = merged.map((r) => {
      if (r.kind === 'daily') {
        return [
          'Daily report',
          new Date(r.ts).toISOString(),
          r.agent_name,
          'Daily report v' + (r.raw?.version || ''),
          '',
          r.raw?.report_text || '',
          '',
          '',
        ];
      }
      const v = r.raw;
      return [
        'Site visit',
        new Date(r.ts).toISOString(),
        r.agent_name,
        v.property_name || '',
        v.report_outcome || '',
        v.report_notes || '',
        v.report_next_steps || '',
        Math.floor((v.duration_seconds || 0) / 60),
      ];
    });
    const csv = [header, ...rows].map((row) => row.map(csvEscape).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `all-reports-${from}_to_${to}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const shareRow = async (row) => {
    let text;
    if (row.kind === 'daily') {
      text = [
        `Daily report — ${row.agent_name}`,
        fmtDateTime(row.ts),
        '',
        row.raw?.report_text || '',
      ].join('\n');
    } else {
      const v = row.raw;
      const lines = [
        `Site visit — ${v.property_name}`,
        `Agent: ${v.agent_name || row.agent_name}`,
        `Submitted: ${fmtDateTime(v.report_submitted_at)}`,
        `Duration: ${fmtDuration(v.duration_seconds)}`,
        `Outcome: ${v.report_outcome || '—'}`,
        '',
        'Notes:', v.report_notes || '(none)',
        '', 'Next steps:', v.report_next_steps || '(none)',
      ];
      if (v.ai_narrative) lines.push('', '--- AI narrative ---', v.ai_narrative);
      text = lines.join('\n');
    }
    try {
      if (navigator.share) await navigator.share({ title: row.title, text });
      else { await navigator.clipboard.writeText(text); toast.success('Copied to clipboard'); }
    } catch (e) { toast.error(`Share failed: ${e.message || e}`); }
  };

  const refreshAll = () => {
    fetchVisitReports();
    fetchDailyRange();
  };

  // Renders one report row — shared by the main agent feed and the
  // "HOD Reports" sub-section so both look and behave identically.
  const renderRow = (row) => {
    const isOpen = expandedKey === row.key;
    const v = row.raw;
    return (
      <div key={row.key} className="rounded-lg border border-slate-200">
        <button
          type="button"
          onClick={() => setExpandedKey(isOpen ? null : row.key)}
          className="w-full text-left p-3 flex items-start gap-3 hover:bg-slate-50"
        >
          {isOpen ? <ChevronDown size={16} className="mt-0.5 text-slate-400" />
                  : <ChevronRight size={16} className="mt-0.5 text-slate-400" />}
          {row.kind === 'visit'
            ? <MapPin size={14} className="text-emerald-600 mt-0.5" />
            : <FileText size={14} className="text-violet-600 mt-0.5" />}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-medium text-slate-900 truncate">{row.title}</span>
              <span className="text-xs text-slate-500">· {row.agent_name}</span>
              <Badge className={`text-xs border ${
                row.kind === 'visit'
                  ? 'bg-emerald-50 text-emerald-700 border-emerald-300'
                  : 'bg-violet-50 text-violet-700 border-violet-300'
              }`}>
                {row.kind === 'visit' ? 'Site visit' : 'Daily report'}
              </Badge>
              {row.isHod && (
                <Badge className="text-xs bg-blue-50 text-blue-700 border-blue-300 border">HOD</Badge>
              )}
              {row.kind === 'visit' && v.report_productive === true && (
                <Badge className="text-xs bg-emerald-50 text-emerald-700 border-emerald-300 border">
                  <CheckCircle2 size={10} className="mr-0.5" /> Productive
                </Badge>
              )}
              {row.kind === 'visit' && v.report_productive === false && (
                <Badge className="text-xs bg-amber-50 text-amber-700 border-amber-300 border">
                  <XCircle size={10} className="mr-0.5" /> Not productive
                </Badge>
              )}
            </div>
            <p className="text-xs text-slate-500 mt-0.5 font-mono">
              {fmtDateTime(row.ts)}
              {row.kind === 'visit' && v.duration_seconds != null && (
                <> · {fmtDuration(v.duration_seconds)} on site</>
              )}
            </p>
            {!isOpen && (
              <p className="text-xs text-slate-600 mt-1 line-clamp-2">{row.subtitle}</p>
            )}
          </div>
          {/* Feedback — opens an inline composer to message
              this report's author directly. */}
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={(e) => {
              e.stopPropagation();
              setFeedbackKey(feedbackKey === row.key ? null : row.key);
              setFeedbackText('');
            }}
            title="Send feedback to the author"
          >
            <MessageSquare size={13} className={feedbackKey === row.key ? 'text-violet-600' : ''} />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={(e) => { e.stopPropagation(); shareRow(row); }}
            title="Copy / share"
          >
            <Share2 size={13} />
          </Button>
        </button>
        {feedbackKey === row.key && (
          <div className="px-4 pb-3 pt-2 border-t border-slate-100 bg-violet-50/40 space-y-2">
            <p className="text-xs font-semibold text-violet-700">
              Send feedback to {row.agent_name || 'author'}
            </p>
            <Textarea
              value={feedbackText}
              onChange={(e) => setFeedbackText(e.target.value)}
              rows={3}
              placeholder={`Write a comment on this ${row.kind === 'visit' ? 'site visit' : 'daily report'}…`}
              className="text-xs"
            />
            <div className="flex justify-end gap-2">
              <Button
                variant="ghost"
                size="sm"
                className="h-7 text-xs"
                onClick={() => { setFeedbackKey(null); setFeedbackText(''); }}
                disabled={feedbackSending}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                className="h-7 text-xs bg-violet-600 hover:bg-violet-700 text-white"
                onClick={() => sendRowFeedback(row)}
                disabled={feedbackSending || !feedbackText.trim()}
              >
                {feedbackSending
                  ? <><RefreshCw size={12} className="mr-1 animate-spin" /> Sending</>
                  : <><Send size={12} className="mr-1" /> Send feedback</>}
              </Button>
            </div>
          </div>
        )}
        {isOpen && (
          <div className="px-4 pb-4 border-t border-slate-100 bg-slate-50/60 text-sm space-y-3 pt-3">
            {row.kind === 'daily' && (
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">Report</p>
                <pre className="text-slate-800 whitespace-pre-wrap font-sans">{v.report_text || '(empty)'}</pre>
              </div>
            )}
            {row.kind === 'visit' && (
              <>
                {v.report_notes && (
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">Agent notes</p>
                    <p className="text-slate-700 whitespace-pre-wrap">{v.report_notes}</p>
                  </div>
                )}
                {v.report_next_steps && (
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">Next steps</p>
                    <p className="text-slate-700 whitespace-pre-wrap">{v.report_next_steps}</p>
                  </div>
                )}
                {v.ai_narrative ? (
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-emerald-700 mb-1">AI narrative</p>
                    <div className="text-slate-800 whitespace-pre-wrap rounded-md bg-white border border-emerald-200 p-3">
                      {v.ai_narrative}
                    </div>
                  </div>
                ) : (
                  <p className="text-xs text-slate-400">AI narrative not generated yet.</p>
                )}
              </>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <Card className="border-slate-200">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <CardTitle className="text-base flex items-center gap-2 flex-wrap">
              <FileText size={18} />
              All Agent Reports
              <HelpInfo
                align="inline"
                title="How to use All Agent Reports"
                steps={[
                  <>One merged, time-ordered feed of everything your team filed — <em>daily reports</em> (manual or AI-generated end-of-day summaries) and <em>site-visit reports</em> — newest first.</>,
                  'The three tiles are clickable filters: Total shows everything, Daily reports and Site visits narrow the list to one kind. The tile counts always reflect the date range, not the active filter, so you can see the full breakdown at a glance.',
                  <>Use the dropdowns to filter by <strong>type</strong> and by <strong>agent</strong>, and the date pickers to set the window. <strong>Clear</strong> empties every filter so you see all-time results.</>,
                  'Click any row to expand it — daily reports show the full text, site visits show notes, outcome, next steps and the AI narrative.',
                  <>Each row has a <strong>Feedback</strong> button (💬) — write a comment and it goes straight to that report\'s author. The share icon copies the report to your clipboard, and <strong>CSV</strong> exports whatever is currently filtered.</>,
                ]}
                tip="Filter to one agent over a 30-day window before a 1:1 — you get their whole activity trail (visits + daily summaries) on one screen."
              />
            </CardTitle>
            <CardDescription>
              Daily reports + site-visit reports across your team
            </CardDescription>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Filter size={14} className="text-slate-400" />
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="h-8 w-[140px] text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All types</SelectItem>
                <SelectItem value="daily">Daily reports</SelectItem>
                <SelectItem value="visit">Site visits</SelectItem>
              </SelectContent>
            </Select>
            <Select value={agentId} onValueChange={setAgentId}>
              <SelectTrigger className="h-8 w-[180px] text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All agents</SelectItem>
                {agents.map((a) => (
                  <SelectItem key={a.id || a.agent_id} value={a.id || a.agent_id}>
                    {a.full_name || a.agent_name || a.email}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Calendar size={14} className="text-slate-400" />
            <input
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              className="h-8 text-xs border border-slate-300 rounded px-2"
            />
            <span className="text-xs text-slate-400">to</span>
            <input
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              className="h-8 text-xs border border-slate-300 rounded px-2"
            />
            {/* Clear filters — empties the date inputs and resets
                type and agent dropdowns to "All". Empty dates fall
                through the date_from / date_to guards in the fetch
                helpers below, so an empty Clear state means all-time
                results (bounded only by the count returned by each
                endpoint). The button is always visible so users can
                find it without trial and error; it dims when there's
                nothing left to clear. */}
            {(() => {
              const atCleared =
                typeFilter === 'all'
                && agentId === 'all'
                && !from
                && !to;
              return (
                <Button
                  variant="ghost"
                  size="sm"
                  className={`h-8 text-xs ${
                    atCleared
                      ? 'text-slate-400 hover:text-rose-600'
                      : 'text-rose-600 hover:text-rose-700'
                  }`}
                  onClick={() => {
                    setTypeFilter('all');
                    setAgentId('all');
                    setFrom('');
                    setTo('');
                  }}
                  title="Clear all filters — show all reports"
                >
                  Clear
                </Button>
              );
            })()}
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={refreshAll} title="Refresh">
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
            </Button>
            <Button variant="outline" size="sm" className="h-8 text-xs" onClick={downloadCsv} disabled={!merged.length}>
              <Download size={12} className="mr-1" /> CSV
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {/* Clickable count tiles — act as quick filter tabs synced with the
            `typeFilter` Select above. Clicking a tile sets the filter, and
            the active tile gets a stronger border + ring so it's obvious
            which view you're in. Clicking the active "Total" tile is a
            no-op since "all" is already showing everything; clicking the
            active Daily/Visit tile clears it back to "all" (toggle off). */}
        <div className="grid grid-cols-3 gap-2 mb-3 text-center" role="tablist" aria-label="Filter reports by type">
          <button
            type="button"
            role="tab"
            aria-selected={typeFilter === 'all'}
            onClick={() => setTypeFilter('all')}
            className={`rounded-md py-2 transition-all ${
              typeFilter === 'all'
                ? 'bg-slate-100 border-2 border-slate-400 ring-2 ring-slate-300/60'
                : 'bg-slate-50 border border-slate-200 hover:bg-slate-100 hover:border-slate-300'
            }`}
          >
            <div className="text-xl font-semibold text-slate-900">{counts.total}</div>
            <div className="text-xs text-slate-500">Total</div>
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={typeFilter === 'daily'}
            onClick={() => setTypeFilter(typeFilter === 'daily' ? 'all' : 'daily')}
            className={`rounded-md py-2 transition-all ${
              typeFilter === 'daily'
                ? 'bg-violet-100 border-2 border-violet-500 ring-2 ring-violet-300/60'
                : 'bg-violet-50 border border-violet-200 hover:bg-violet-100 hover:border-violet-300'
            }`}
          >
            <div className="text-xl font-semibold text-violet-700">{counts.daily}</div>
            <div className="text-xs text-violet-700">Daily reports</div>
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={typeFilter === 'visit'}
            onClick={() => setTypeFilter(typeFilter === 'visit' ? 'all' : 'visit')}
            className={`rounded-md py-2 transition-all ${
              typeFilter === 'visit'
                ? 'bg-emerald-100 border-2 border-emerald-500 ring-2 ring-emerald-300/60'
                : 'bg-emerald-50 border border-emerald-200 hover:bg-emerald-100 hover:border-emerald-300'
            }`}
          >
            <div className="text-xl font-semibold text-emerald-700">{counts.visit}</div>
            <div className="text-xs text-emerald-700">Site visits</div>
          </button>
        </div>

        {merged.length === 0 ? (
          <p className="text-sm text-slate-500 text-center py-8">
            No reports in this filter window.
          </p>
        ) : (
          <div className="space-y-2">
            {merged.map(renderRow)}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default UnifiedReportsTab;
